<template>
  <div>
    <b-row>
      <b-col>
        <div
          class="carousel-subportion-card component-bg--hover full-bordered"
        >
          <div class="image-area">
            <img
              v-if="flagDemo && slideThumbnails.length===0"
              alt="demo thumbnail"
              class="microdemo-thumbnail"
              src="@/img/subportionplaceholder.jpg"
              aria-hidden="true"
              @click="openDemoViewer()"
            >
            <img
              v-else-if="slideThumbnails.length===0"
              alt="no-image thumbnail"
              class="microdemo-thumbnail"
              src="@/img/slide-placeholder_small.jpg"
              aria-hidden="true"
            >
            <b-carousel
              v-else
              v-model="slide"
              :controls="slideThumbnails.length > 1"
            >
              <b-carousel-slide
                v-for="(slideThumbnail, index) in slideThumbnails"
                :key="index"
              >
                <template #img>
                  <div
                    class="img-wrapper"
                  >
                    <img
                      alt="thumbnail"
                      class="d-block img-wrapper__img"
                      :src="'data:image/jpeg;base64, ' + slideThumbnail.thumbnailBytes"
                      aria-hidden="true"
                      @click="onThumbnailSelected(slideThumbnail)"
                    >
                  </div>
                </template>
              </b-carousel-slide>
            </b-carousel>
          </div>
          <div class="footer-area">
            <div class="text-truncate">
              <span
                qa="subportion-card-assay-name-span"
                class="ml-1 assay-name primary-dark-color"
              >{{ subportionData.assay.name }}</span>
            </div>
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <span class="icon-porta" />
                <span qa="subportion-card-label-span">{{ subportionData.number }}</span>
                <vt-bubble
                  qa="subportion-card-incident-div"
                  class="ml-1"
                  :tooltip="` ${$t('grid.detail.hasAlarms')}`"
                  :number="subportionData.incidentsCount"
                  :type="true"
                  @click="$emit('showIncidentsModalForSubportion', subportionData.id)"
                />
                <vt-bubble
                  qa="subportion-card-attachment-div"
                  :tooltip="` ${$tc('global.file', 2)}`"
                  :number="subportionData.attachmentsCount"
                  :type="false"
                  @click="$emit('showSubportionAttachmentTreeModal', subportionData.id)"
                />
              </div>
              <div class="d-flex align-items-center">
                <i
                  v-if="subportionData.currentLaboratory.labId !== labId"
                  v-tooltip="`${$t('global.elementLocatedIn')} ${subportionData.currentLaboratory.labName}`"
                  aria-hidden="true"
                  class="fas fa-map-marker-alt alt-color mr-2"
                />
                <span
                  v-if="subportionData.view"
                  v-tooltip="` ${$t('diagnosis.btns.viewed')}: 00-00-0000 00:00`"
                  qa="subportion-card-viewed-span"
                  class="far fa-check view mr-1"
                />
                <span
                  v-else
                  v-tooltip="$t('diagnosis.btns.noViewed')"
                  qa="subportion-card-no-viewed-span"
                  class="far fa-check no-view mr-1"
                />
                <div class="mx-1">
                  <template v-if="subportionData.revisions.length">
                    <button
                      id="pop-review-portion"
                      qa="subportion-card-review-button"
                      class="btn btn-round p-0"
                      :class="[allEntityRevisionsCompleted(subportionData.revisions) ? 'completed' : 'pending']"
                      type="button"
                      @click="onShowRevisionPopoverClick(subportionData, ENTITY_TYPE_SUBPORTION, subportionData.revisions)"
                    >
                      <span
                        v-if="allEntityRevisionsCompleted(subportionData.revisions)"
                        v-tooltip="$tc('diagnosis.labels.reviewCompleted', 1)"
                        class="far fa-tasks"
                      />
                      <span
                        v-else
                        v-tooltip="$tc('diagnosis.labels.pendingReview')"
                        class="far fa-tasks"
                      />
                    </button>
                    <b-popover
                      v-if="subportionData.revisions.length > 1"
                      :show.sync="showRevisionsPopover"
                      target="pop-review-portion"
                      placement="top"
                      triggers="click blur"
                    >
                      <div class="review-pop">
                        <ul>
                          <li
                            v-if="subportionData.revisions.find(revision => !revision.finished)"
                            @click="mapAndShowRevisionInfo(subportionData, ENTITY_TYPE_SUBPORTION, subportionData.revisions[0])"
                          >
                            {{ $t('diagnosis.labels.reviewInformation') }}
                          </li>
                          <template v-if="subportionData.revisions.filter(revision => revision.finished).length">
                            <hr class="my-0">
                            <div class="p-1 mx-3">
                              <span class="main">{{ $tc('diagnosis.labels.reviewCompleted', 2) }}</span>
                            </div>
                            <li
                              v-for="(revision, index) in subportionData.revisions.filter(aRevision => aRevision.finished)"
                              :key="revision.id"
                              @click="mapAndShowRevisionInfo(subportionData, ENTITY_TYPE_SUBPORTION, revision)"
                            >
                              {{ $t('diagnosis.labels.review') }} {{ index + 1 }}
                            </li>
                          </template>
                        </ul>
                      </div>
                    </b-popover>
                  </template>
                </div>
                <b-button
                  v-if="visibleSubportionAnalysisStatusButton"
                  v-tooltip="subportionAnalysisStatusButtonTooltip"
                  qa="subportion-card-ia-button"
                  class="btn__icon py-0 d-flex align-items-center"
                  :style="{ color: subportionAnalysisStatusColor }"
                  @click="onAnalysisResultButtonClick"
                >
                  <span class="fas fa-brain" />
                  <div class="ml-1 dark">
                    <span class="font-weight--medium">{{ subportionDataAnalysisResultCount }}</span>
                    <span class="font-size--regular-1">{{ `/${subportionDataAnalysisResultTotalCount}` }}</span>
                  </div>
                </b-button>
                <b-button
                  :id="subportionData.label"
                  qa="subportion-card-ellipsis-button"
                  class="btn__icon btn__icon--primary py-0"
                >
                  <span class="fas fa-ellipsis-v" />
                </b-button>
                <b-popover
                  :target="subportionData.label"
                  placement="bottom"
                  triggers="click blur"
                >
                  <div class="ap-popover">
                    <ul>
                      <li
                        v-if="canRequestReview"
                      >
                        <b-button
                          qa="subportion-card-request-review-a"
                          @click="showReviewModal"
                        >
                          {{ $t('diagnosis.btns.review') }}
                        </b-button>
                      </li>
                      <li
                        v-if="$_canCreateIncident()"
                      >
                        <b-button
                          qa="subportion-card-incident-a"
                          :disabled="!$_canBeIncidentCreatedToSubportion(subportionData)"
                          @click="$emit('openIncidentFormModal', { coreEntity: 'SUBPORTION', entitiesIds: {casId, samId, porId, supId: subportionData.id}})"
                        >
                          {{ $t('diagnosis.btns.incident') }}
                        </b-button>
                      </li>
                      <li
                        v-if="$_canAttachDocumentToSubportion()"
                      >
                        <b-button
                          qa="subportion-card-attachment-a"
                          :disabled="!$_canBeDocumentAttachedToSubportion(subportionData)"
                          @click="$emit('openAddFileModal', [labId, casId, samId, porId, subportionData.id])"
                        >
                          {{ $t('diagnosis.btns.attach') }}
                        </b-button>
                      </li>
                      <li>
                        <b-button
                          qa="ssubportion-card-global-locator"
                          @click="showInGlobalLocator(subportionData.label)"
                        >
                          {{ $tc('global.showInGlobalLocator') }}
                        </b-button>
                      </li>
                    </ul>
                  </div>
                </b-popover>
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import VtBubble from '@/components/common/VtBubble'
import subportionAttachmentService from '@/api/resources/attachment/subportionAttachmentService'
import DiagnosisSubportionService from '@/modules/diagnosisModule/services/DiagnosisSubportionService'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import { SubportionOperationGuardMixin } from '@/api/operations/mixins/guards/subportion-operation-guard.mixin'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import { ViewerType } from '@/modules/viewerModule/ViewerModule.models'
import { viewerService } from '@/modules/viewerModule/services/viewer.service'
import { ATTACHMENT_MIME_TYPES } from '@/api/resources/attachment/attachmentTypeService'
import { some } from 'lodash'

export default {
    name: 'SubportionCard',
    components: {
        VtBubble
    },
    mixins: [
        SubportionOperationGuardMixin,
        IncidentOperationGuardMixin
    ],
    props: {
        subportionData: { type: Object, required: true },
        showPortion: { type: Boolean, default: false },
        labId: { required: true, type: Number },
        casId: { required: true, type: Number },
        samId: { required: true, type: Number },
        porId: { required: true, type: Number }
    },
    data() {
        return {
            showRevisionsPopover: false,
            flagDemo: false,
            slide: 0,
            slideThumbnails: [],
            displayAnalysisSidepanel: false
        }
    },
    computed: {
        subportionAnalysisStatusButtonTooltip() {
            return this.subportionData.analysisStatusAI?.name ? this.$tc(this.subportionData.analysisStatusAI.name) : null
        },
        subportionAnalysisStatusColor() {
            return this.subportionData.analysisStatusAI?.color
        },
        subportionDataAnalysisResultCount() {
            return this.subportionData.analysisResult ? this.subportionData.analysisResult.filter(result => result.analysisStatus?.id === this.subportionData.analysisStatusAI?.id).length : 0
        },
        subportionDataAnalysisResultTotalCount() {
            return this.subportionData.analysisResult ? this.subportionData.analysisResult.length : 0
        },
        visibleSubportionAnalysisStatusButton() {
            return !!this.subportionData.analysisStatusAI
        },
        isArtificialIntelligence() {
            return some(this.subportionData.analysisResult, (value, index) => {
                // TODO Map to enum analysis status codes
                const allowedValues = ['03', '04']
                return allowedValues.includes(value.analysisStatus.code)
            })
        },
        ENTITY_TYPE_SUBPORTION() {
            return ATTACHMENT_ENTITY_TYPE.SUBPORTION
        },
        canRequestReview() {
            return this.subportionData.metadata.operationGuards.canCreateRevision && this.$_canRequestAnySubportionRevision()
        }
    },
    watch: {
        showPortion(newValue) {
            if (newValue) {
                this.loadAttachments()
            }
        }
    },
    async mounted() {
        this.flagDemo = (this.$_globalParameters.demoEnvironment === true)
    },
    methods: {
        onAnalysisResultButtonClick() {
            if (this.subportionData.analysisResult.length) {
                this.$emit('openAnalysisSidePanel', this.subportionData)
            }
        },
        showInGlobalLocator(label) {
            this.$eventHub.$emit('show-in-global-locator', label)
        },
        async loadAttachments() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(subportionAttachmentService.getAttachments(this.labId, this.casId, this.samId, this.porId, this.subportionData.id))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
            }
            if (response.length > 0) {
                this.slideThumbnails = response.filter(resource => resource.mimetype === ATTACHMENT_MIME_TYPES.URI || resource.mimetype.startsWith(ATTACHMENT_MIME_TYPES.IMAGE))
            } else {
                this.slideThumbnails = []
            }
        },
        async onThumbnailSelected(slide) {
            if (slide.mimetype === ATTACHMENT_MIME_TYPES.URI && !this.subportionData.useInternalViewer) {
                const response = await this.$_toCallUnique(subportionAttachmentService.getUrlForOpenViewer(this.labId, this.casId, this.samId, this.porId, this.subportionData.id, slide.attachmentId))
                if (response) {
                    this.$_openUrl({ url: response })
                }
            } else if (slide.mimetype === ATTACHMENT_MIME_TYPES.URI && this.subportionData.useInternalViewer) {
                viewerService.openViewer({
                    router: this.$router,
                    viewerType: ViewerType.WSI_VIEWER,
                    laboratoryId: this.labId,
                    caseId: this.casId,
                    sampleId: this.samId,
                    attachmentId: slide.attachmentId,
                    label: this.subportionData.label,
                    portionId: this.porId,
                    subportionId: this.subportionData.id
                })
            } else if (slide.mimetype.startsWith(ATTACHMENT_MIME_TYPES.IMAGE)) {
                viewerService.openViewer({
                    router: this.$router,
                    viewerType: ViewerType.STATIC_VIEWER,
                    laboratoryId: this.labId,
                    caseId: this.casId,
                    sampleId: this.samId,
                    attachmentId: slide.attachmentId,
                    label: this.subportionData.label,
                    portionId: this.porId,
                    subportionId: this.subportionData.id
                })
            }
        },
        mapAndShowRevisionInfo(entity, entityType, revision) {
            const reviewInfoData = {
                revision: revision,
                entitySelected: {
                    ...entity,
                    subportionId: entity.id,
                    samId: this.samId,
                    porId: this.porId
                },
                entityType: entityType
            }
            this.$emit('showReviewInfoModal', reviewInfoData)
        },
        onShowRevisionPopoverClick(entity, entityType) {
            if (entity.revisions.length === 1) {
                this.mapAndShowRevisionInfo(entity, entityType, entity.revisions[0])
            }
        },
        allEntityRevisionsCompleted(revisions) {
            return revisions.filter(revision => revision.finished).length === revisions.length
        },
        openDemoViewer() {
            viewerService.openViewer({
                router: this.$router,
                viewerType: ViewerType.WSI_VIEWER,
                laboratoryId: this.labId,
                caseId: this.casId,
                sampleId: this.samId,
                attachmentId: undefined, // TODO The attachment id cannot be undefined(only number). Pending review
                label: this.subportionData.label,
                portionId: this.porId,
                subportionId: this.subportionData.id
            })
        },
        async showReviewModal() {
            this.$emit('showReviewModal')
        },
        async requestReview() {
            const [err] = await this.toCall(DiagnosisSubportionService.requestReview(this.labId, this.casId, this.samId, this.porId, this.subportionData.id))
            if (err) {
                this.showErrorModalGlobal()
            } else {
                this.$emit('reviewRequested')
            }
        },
        toggleAnalysisSidepanel() {
            this.displayAnalysisSidepanel = !this.displayAnalysisSidepanel
        }
    }
}
</script>

<style lang="scss">
    .vt-side-panel {
        &.analysis-side-panel {
            width: 470px !important;
            .form-label + span {
                color: vt-color(dark-text);
                font-size: font-size(very-small);
            }
            .analysis-side-panel__card {
                border-left-width: 4px;
                border-left-style: solid;
                border-color: vt-color(primary);
            }
        }
    }
    .carousel-subportion-card {
        @include custom-card($spread: -4px);
        .carousel-control-prev, .carousel-control-next {
            width: 10%;
        }
        .carousel-control-prev, .carousel-control-next {
            transition: .2s;
        }
        .carousel-control-prev:hover, .carousel-control-next:hover {
            background: #000000;
            opacity: .4;
        }
    }
    .carousel-subportion-card .footer-area {
        padding: 7px;
        border-top: 1px solid vt-color(general-border, .5);
        cursor: default;
        position: relative;

        .text-truncate {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 200px;

            .assay-name {
                font-weight: bold;
                font-size: font-size(very-small);
            }
        }

    }
    .carousel-subportion-card .carousel {
        height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: vt-color(black, .8);
        overflow: hidden;

        .carousel-control-prev-icon, .carousel-control-next-icon {
            width: 15px;
        }

        .carousel-caption{
            display: none;
        }

        .carousel-inner {
            height: 100%;

            .carousel-item {
                height: 100%;

                .img-wrapper {
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    img {
                        width: 100%;
                        height: auto;
                    }

                    .analysis-badge {
                        width: 25px;
                        height: 25px;
                        bottom: 8px;
                        right: 30px;
                        border-radius: 50%;
                        background: vt-color(white-light);
                        box-shadow: 0 0 2px 2px vt-color(card-box-shadow, .1);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        position: absolute;
                        z-index: 2;
                    }
                }
            }
        }

        .carousel-indicators {
            margin: 3px;

            li {
                background-color: transparent;
                border: 0;
                padding: 3px;
                width: 10px;
                height: 8px;

                &::before {
                    content: '';
                    display: block;
                    background: vt-color(white-light);
                    height: 7px;
                    width: 7px;
                    border-radius: 50%;
                }
            }
        }
    }
    .subportion-card .btn-round.pending {
        color: vt-color(orange-medium);
    }
    .subportion-card .btn-round.completed {
        color: vt-color(green-radiant);
    }
</style>

<style lang="scss" scoped>
.microdemo-thumbnail{
    width:100%;
    height:100px;
}
.assayName{
  font-size: font-size(very-small);
  color: vt-color(primary);
}

.assayCol{
  margin-bottom: -7px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subportion-view-card a {
  color: vt-color(white-light);
  font-size: font-size(small);
}
.frame {
  border: 1px solid rgba(0, 0, 0, 0.125);
  height: 80px;
  overflow: hidden;
}

.card-body {
  padding: 5px;
}

.subportion-view-card .subportion-hover {
  width: 0;
  height: 0;
  overflow: hidden;
  cursor: pointer;
  transition: 0.1s;
  background: rgba(0, 0, 0, 0.6);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border-radius: 20%;
  color: #FFF;
}

.subportion-view-card .content {
  display: none;
}

@keyframes card-hover {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.subportion-view-card:hover .content {
  display: block;
  animation: card-hover 1s ease;
}

.subportion-view-card:hover .subportion-hover {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 0;
}

.subportion-view-card {
  position: relative;
}
.frame {
  border: 1px solid rgba(0, 0, 0, 0.125);
  height: 80px;
  overflow: hidden;
}

.card-body {
  padding: 0;
}

.subportion-card {
  box-shadow: 1px 1px 5px 0 rgba(125, 125, 125, 0.64);
  transition: 0.2s;
}

.subportion-card:hover {
  box-shadow: 1px 1px 5px 0 rgba(125, 125, 125, 2);
}

.subportion-card .btn {
  font-size: font-size(very-normal) !important;
  margin-right: 0 !important;
}

.view {
  color: vt-color(green-radiant);
}

.no-view {
  color: vt-color(light-grey);
}
</style>
